import { useCallback, useEffect, useRef, useState } from 'react';
import { newMessageId } from '../InlineChat';
import type { Message } from '../InlineChat';
import type { BackgroundAnalysisJobView } from './ArkoBackgroundAnalysisChip';
import {
  MAX_PARALLEL_BACKGROUND_JOBS,
  buildBackgroundAnalysisChatContent,
  buildBackgroundJobSnapshot,
  loadBackgroundJobQueue,
  runBackgroundDocumentAnalysis,
  saveBackgroundJobQueue,
  shortDocumentLabel,
  threadHasBackgroundJobResult,
  type BackgroundJobCompletionNotice,
  type BackgroundJobSnapshot,
} from './portalBackgroundJobs';
import {
  backgroundAnalysisCoverageHint,
  type AnalysisProgress,
} from './portalBackgroundAnalysisChunks';

interface UsePortalBackgroundJobsArgs {
  customerId: string;
  webhookUrl: string;
  timeoutMs: number;
  mode?: string;
  extraBody?: Record<string, unknown>;
  getThreadMessages: (threadId: string) => Message[] | undefined;
  appendToThread: (threadId: string, updater: (prev: Message[]) => Message[]) => void;
  /** Nach Chat-Post: Doc-Kasten einklappen, damit die ARKO-Nachricht sichtbar ist. */
  onAnalysisPostedToChat?: () => void;
}

export function usePortalBackgroundJobs({
  customerId,
  webhookUrl,
  timeoutMs,
  mode,
  extraBody,
  getThreadMessages,
  appendToThread,
  onAnalysisPostedToChat,
}: UsePortalBackgroundJobsArgs) {
  const [pendingJobs, setPendingJobs] = useState<BackgroundJobSnapshot[]>(() =>
    loadBackgroundJobQueue(customerId),
  );
  const [finishedViews, setFinishedViews] = useState<BackgroundAnalysisJobView[]>([]);
  const [progressById, setProgressById] = useState<Record<string, AnalysisProgress>>({});
  const [openJobId, setOpenJobId] = useState<string | null>(null);
  const [completionNotices, setCompletionNotices] = useState<BackgroundJobCompletionNotice[]>([]);
  const controllersRef = useRef<Map<string, AbortController>>(new Map());
  const cancelledRef = useRef<Set<string>>(new Set());
  const runningRef = useRef<Set<string>>(new Set());
  const resumeStartedRef = useRef(false);
  const onPostedRef = useRef(onAnalysisPostedToChat);
  onPostedRef.current = onAnalysisPostedToChat;

  const persistQueue = useCallback((jobs: BackgroundJobSnapshot[]) => {
    saveBackgroundJobQueue(customerId, jobs);
  }, [customerId]);

  const removeJob = useCallback((requestId: string) => {
    setPendingJobs((prev) => {
      const next = prev.filter((job) => job.request_id !== requestId);
      persistQueue(next);
      return next;
    });
  }, [persistQueue]);

  const pushNotice = useCallback((notice: BackgroundJobCompletionNotice) => {
    setCompletionNotices((prev) => [...prev, notice]);
  }, []);

  const dismissNotice = useCallback((id: string) => {
    setCompletionNotices((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const upsertFinished = useCallback((view: BackgroundAnalysisJobView) => {
    setFinishedViews((prev) => {
      const without = prev.filter((item) => item.request_id !== view.request_id);
      return [...without, view];
    });
  }, []);

  const executeJob = useCallback((job: BackgroundJobSnapshot) => {
    if (runningRef.current.has(job.request_id)) return;
    if (threadHasBackgroundJobResult(getThreadMessages(job.threadId), job.request_id)) {
      removeJob(job.request_id);
      return;
    }

    runningRef.current.add(job.request_id);
    const controller = new AbortController();
    controllersRef.current.set(job.request_id, controller);

    void (async () => {
      const outcome = await runBackgroundDocumentAnalysis({
        job,
        webhookUrl,
        timeoutMs,
        signal: controller.signal,
        isCancelled: () => cancelledRef.current.has(job.request_id),
        onProgress: (progress) => {
          setProgressById((prev) => ({ ...prev, [job.request_id]: progress }));
        },
      });

      const { documentName, threadId, request_id: requestId } = job;
      controllersRef.current.delete(requestId);
      cancelledRef.current.delete(requestId);
      runningRef.current.delete(requestId);
      removeJob(requestId);
      setProgressById((prev) => {
        if (!(requestId in prev)) return prev;
        const next = { ...prev };
        delete next[requestId];
        return next;
      });

      if (outcome.kind === 'success') {
        const chat = buildBackgroundAnalysisChatContent(
          documentName,
          outcome.reply,
          outcome.coverage
            ? backgroundAnalysisCoverageHint(outcome.coverage)
            : null,
        );
        upsertFinished({
          request_id: requestId,
          documentName,
          status: 'done',
          workerText: outcome.workerText,
          coverage: outcome.coverage,
        });
        appendToThread(threadId, (prev) => [
          ...prev,
          {
            role: 'bot',
            id: newMessageId(),
            reasoningTrace: outcome.trace,
            ...chat,
          },
        ]);
        onPostedRef.current?.();
        pushNotice({
          id: `bg_done_${requestId}`,
          threadId,
          documentName,
          success: true,
          summary: `Analyse „${shortDocumentLabel(documentName)}“ abgeschlossen`,
        });
        setOpenJobId(requestId);
        return;
      }

      if (outcome.cancelled) {
        upsertFinished({
          request_id: requestId,
          documentName,
          status: 'cancelled',
          workerText: outcome.workerText
            || `Die Hintergrundanalyse „${documentName}“ wurde abgebrochen.`,
          coverage: outcome.coverage,
        });
        appendToThread(threadId, (prev) => [
          ...prev,
          {
            role: 'bot',
            text: `Die Hintergrundanalyse „${documentName}“ wurde abgebrochen.`,
            id: newMessageId(),
            reasoningTrace: outcome.trace,
          },
        ]);
        return;
      }

      const failText = outcome.workerText || (outcome.timedOut
        ? `Die Hintergrundanalyse „${documentName}“ hat das Zeitlimit erreicht. Bitte erneut starten.`
        : `Die Hintergrundanalyse „${documentName}“ ist fehlgeschlagen. Bitte erneut versuchen.`);
      upsertFinished({
        request_id: requestId,
        documentName,
        status: 'error',
        workerText: failText,
        coverage: outcome.coverage,
      });
      appendToThread(threadId, (prev) => [
        ...prev,
        {
          role: 'bot',
          text: failText,
          id: newMessageId(),
          reasoningTrace: outcome.trace,
        },
      ]);
      pushNotice({
        id: `bg_fail_${requestId}`,
        threadId,
        documentName,
        success: false,
        summary: outcome.timedOut
          ? `Analyse „${shortDocumentLabel(documentName)}“ — Zeitlimit`
          : `Analyse „${shortDocumentLabel(documentName)}“ fehlgeschlagen`,
      });
      setOpenJobId(requestId);
    })();
  }, [
    appendToThread,
    getThreadMessages,
    pushNotice,
    removeJob,
    timeoutMs,
    upsertFinished,
    webhookUrl,
  ]);

  const startSlots = useCallback(() => {
    setPendingJobs((current) => {
      const runnable = current.filter((job) => !runningRef.current.has(job.request_id));
      const slots = MAX_PARALLEL_BACKGROUND_JOBS - runningRef.current.size;
      if (slots <= 0 || runnable.length === 0) return current;
      runnable.slice(0, slots).forEach((job) => executeJob(job));
      return current;
    });
  }, [executeJob]);

  const enqueueJob = useCallback((args: {
    documentId: string;
    documentName: string;
    threadId: string;
    history?: Message[];
    documentContext: string;
    openDocumentsMeta?: import('./portalDocuments').PortalOpenDocumentMeta[];
    selectedDocuments?: import('./portalDocuments').PortalSelectedDocumentPayload[];
    documentExtractFingerprint?: string | null;
    extractPages?: number;
    extractRawChars?: number;
    extractTruncated?: boolean;
    extractSource?: string;
  }): BackgroundJobSnapshot | null => {
    let created: BackgroundJobSnapshot | null = null;
    setPendingJobs((prev) => {
      if (prev.length >= MAX_PARALLEL_BACKGROUND_JOBS) return prev;
      const job = buildBackgroundJobSnapshot({
        ...args,
        history: [],
        extraBody,
        mode,
      });
      created = job;
      const next = [...prev, job];
      persistQueue(next);
      return next;
    });
    if (created) {
      const job = created as BackgroundJobSnapshot;
      setOpenJobId(job.request_id);
      window.setTimeout(startSlots, 0);
    }
    return created;
  }, [extraBody, mode, persistQueue, startSlots]);

  const cancelJob = useCallback((requestId: string) => {
    const controller = controllersRef.current.get(requestId);
    if (controller) {
      cancelledRef.current.add(requestId);
      controller.abort();
      return;
    }
    removeJob(requestId);
  }, [removeJob]);

  const dismissFinished = useCallback((requestId: string) => {
    setFinishedViews((prev) => prev.filter((item) => item.request_id !== requestId));
    setOpenJobId((current) => (current === requestId ? null : current));
  }, []);

  useEffect(() => {
    persistQueue(pendingJobs);
  }, [pendingJobs, persistQueue]);

  useEffect(() => {
    setPendingJobs(loadBackgroundJobQueue(customerId));
    setFinishedViews([]);
    setProgressById({});
    setOpenJobId(null);
    resumeStartedRef.current = false;
  }, [customerId]);

  useEffect(() => {
    if (resumeStartedRef.current) return;
    resumeStartedRef.current = true;
    if (pendingJobs.length === 0) return;
    startSlots();
  }, [pendingJobs, startSlots]);

  useEffect(() => () => {
    controllersRef.current.forEach((controller) => controller.abort());
    controllersRef.current.clear();
    cancelledRef.current.clear();
    runningRef.current.clear();
  }, []);

  const canEnqueue = pendingJobs.length < MAX_PARALLEL_BACKGROUND_JOBS;
  const hasRunningJobs = pendingJobs.length > 0;
  const analysisViews: BackgroundAnalysisJobView[] = [
    ...pendingJobs.map((job) => ({
      request_id: job.request_id,
      documentName: job.documentName,
      status: 'running' as const,
      workerText: progressById[job.request_id]?.notes ?? null,
      progress: progressById[job.request_id] ?? null,
    })),
    ...finishedViews,
  ];

  return {
    pendingJobs,
    analysisViews,
    openJobId,
    setOpenJobId,
    completionNotices,
    canEnqueue,
    hasRunningJobs,
    enqueueJob,
    cancelJob,
    dismissFinished,
    dismissNotice,
  };
}
